/**
 * Multi-factor DEMA-WMA Gainer > "Rules in Effect" tab: the effective configuration in collapsible sections
 * (reusing the existing settings cards, profit-booking card and version actions unchanged -- every save still
 * creates a NEW version on the server, every activation still needs the typed confirmation).
 */
import { useState, type ReactNode } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { DemaWmaOverview } from "@/services/demaWmaGainerService";
import { RULE_SET_V2, RULE_SET_V6, SectionCard, rulesSummary, sectionsFor } from "./demaWmaRules";
import DemaWmaProfitBookingCard from "./DemaWmaProfitBookingCard";

type Version = DemaWmaOverview["versions"][number];

const ist = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export function ruleSetLabel(rs?: string): string {
  return rs === RULE_SET_V6 ? "v6 (CE+PE)" : rs === RULE_SET_V2 ? "v2 (CE-only)" : "v1 (DEMA cross)";
}

/** Plain status of one saved version: ACTIVE = used by the paper runner (and customers); LIVE APPROVED = the
 *  version carrying the live approval; otherwise SAVED (inactive). */
export function versionRoles(v: Version, ov: DemaWmaOverview): string[] {
  const roles: string[] = [];
  if (v.is_active) roles.push("ACTIVE");
  if (ov.live_trading?.enabled && ov.live_trading.approved_version === v.version) roles.push("LIVE APPROVED");
  if (roles.length === 0) roles.push("SAVED");
  return roles;
}

const ROLE_COLOR: Record<string, "success" | "error" | "default"> = { ACTIVE: "success", "LIVE APPROVED": "error", SAVED: "default" };

/** Section ids, in display order (also the accordion test ids). */
export const RULE_SECTIONS = [
  { id: "indicators", title: "1. Instruments & Indicators" },
  { id: "rules", title: "2. Entry & Exit Rules" },
  { id: "session", title: "3. Trading Window" },
  { id: "limits", title: "4. Limits & Capital" },
  { id: "execution", title: "5. Execution" },
  { id: "profit-booking", title: "6. Profit Booking" },
  { id: "versions", title: "7. Configuration Version Management" },
] as const;

export interface DemaWmaRulesTabProps {
  overview: DemaWmaOverview;
  onSaved: (msg: string) => Promise<void>;
  onActivate: (version: number) => void;
  onSaveV2Draft: () => void;
  /** Sections open on first render (default: Entry & Exit Rules). */
  defaultOpen?: string[];
}

export default function DemaWmaRulesTab({ overview: ov, onSaved, onActivate, onSaveV2Draft, defaultOpen = ["rules"] }: DemaWmaRulesTabProps) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(defaultOpen));
  const toggle = (id: string) => setOpen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  const allOpen = open.size === RULE_SECTIONS.length;
  const sections = Object.fromEntries(sectionsFor(ov.rule_set).map((s) => [s.id, s]));
  const active = ov.versions.find((v) => v.is_active);
  const tf = Number(ov.config?.timeframe_minutes ?? 5) === 1 ? 1 : 5;

  const card = (id: string) => sections[id] && <SectionCard section={sections[id]} overview={ov} onSaved={onSaved} />;

  const body: Record<string, ReactNode> = {
    indicators: (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {(Array.isArray(ov.fields.instruments) ? ov.fields.instruments : []).join(", ") || "—"} · {tf}-minute index candles,
          evaluated when a candle has closed. Indicators are calculated on the index, not on option prices.
        </Typography>
        {ov.instruments_info.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Lot size / strike step (instrument master):{" "}
            {ov.instruments_info.map((i) => `${i.symbol_code} ${i.lot_size} / ${i.strike_step}`).join(", ")}
          </Typography>
        )}
        {card("indicators")}
      </>
    ),
    rules: (
      <>
        <Box component="ul" sx={{ m: 0, mb: 2, pl: 3 }} data-testid="rules-summary">
          {rulesSummary(ov.fields, ov.rule_set, ov.config).map((line) => (
            <Typography component="li" variant="body2" key={line} sx={{ mb: 0.5 }}>{line}</Typography>
          ))}
        </Box>
        {card("rules")}
      </>
    ),
    session: card("session"),
    limits: card("limits"),
    execution: card("execution"),
    "profit-booking": (
      <DemaWmaProfitBookingCard versions={ov.versions} activeVersion={ov.active_version}
                                onSaved={(msg) => { void onSaved(msg); }} />
    ),
    versions: (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          ACTIVE = the version the paper runner and every customer activation trade with. LIVE APPROVED = the version that
          carries the live-trading approval. SAVED = stored but not in use. Activating needs a typed confirmation and is
          refused while a position is open; start / stop state never changes.
        </Typography>
        {ov.versions.length === 0 ? (
          <Alert severity="info">No saved versions yet.</Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader data-testid="versions-table">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell><TableCell>Status</TableCell><TableCell>Rule set</TableCell><TableCell>TF</TableCell>
                  <TableCell>Created</TableCell><TableCell>Activated</TableCell><TableCell>Notes</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {[...ov.versions].sort((a, b) => b.version - a.version).map((v) => (
                  <TableRow key={v.version} selected={v.is_active}>
                    <TableCell>v{v.version}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {versionRoles(v, ov).map((r) => <Chip key={r} size="small" label={r} color={ROLE_COLOR[r]} variant={r === "SAVED" ? "outlined" : "filled"} />)}
                      </Stack>
                    </TableCell>
                    <TableCell>{ruleSetLabel(v.rule_set)}</TableCell>
                    <TableCell>{v.timeframe_minutes ? `${v.timeframe_minutes}m` : "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{ist(v.created_at)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{ist(v.activated_at)}</TableCell>
                    <TableCell sx={{ maxWidth: 520 }}><Typography variant="caption">{v.notes ?? "—"}</Typography></TableCell>
                    <TableCell>
                      {!v.is_active && (
                        <Button size="small" color="warning" onClick={() => onActivate(v.version)}>Activate…</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Side-by-side comparison of two versions is not available yet (the server does not expose stored version settings);
          each version&apos;s notes describe what changed from its source.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }} alignItems={{ sm: "center" }}>
          <Button size="small" variant="outlined" onClick={onSaveV2Draft}>Save v2 draft (inactive)</Button>
          <Typography variant="caption" color="text.secondary">
            Creates a new INACTIVE version with the v2 rules (CE-only, stop-loss 10 %, no target). Activating is a separate,
            confirmed step.
          </Typography>
        </Stack>
      </>
    ),
  };

  return (
    <Box data-testid="rules-tab">
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" fontWeight={700}>Effective configuration</Typography>
        <Chip size="small" color="success" label={`Active v${ov.active_version ?? "—"}`} data-testid="effective-version" />
        <Chip size="small" variant="outlined" label={ruleSetLabel(ov.rule_set)} />
        <Chip size="small" variant="outlined" label={`Runner: ${ov.execution_status}`} />
        {ov.live_trading?.enabled
          ? <Chip size="small" color="error" label={`Live approved on v${ov.live_trading.approved_version ?? "—"}`} />
          : <Chip size="small" color="info" label="Paper (live not approved)" />}
        <Typography variant="caption" color="text.secondary">
          Last updated {ist(active?.activated_at ?? active?.created_at)} · hash {ov.config_hash.slice(0, 10)}…
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={() => setOpen(allOpen ? new Set() : new Set(RULE_SECTIONS.map((s) => s.id)))}>
          {allOpen ? "Collapse all" : "Expand all"}
        </Button>
      </Stack>
      {RULE_SECTIONS.map((s) => (
        <Accordion key={s.id} expanded={open.has(s.id)} onChange={() => toggle(s.id)} disableGutters
                   // profit booking loads its own data: only when opened. Settings cards stay mounted so unsaved edits survive a collapse.
                   TransitionProps={{ unmountOnExit: s.id === "profit-booking" }} data-testid={`rules-acc-${s.id}`}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`rules-${s.id}-content`} id={`rules-${s.id}-header`}>
            <Typography fontWeight={600}>{s.title}</Typography>
          </AccordionSummary>
          <AccordionDetails id={`rules-${s.id}-content`}>{body[s.id]}</AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
